import React, { useState } from "react";
import { Link } from "react-router-dom";
import FarmersNav from "@/components/FarmersNav";
import { fpPath } from "@/lib/site";
import { TOOLKIT, STAGES, CATEGORIES } from "@/data/farmerToolkit";
import PhoneMock from "@/components/toolkit/PhoneMock";
import ToolkitOnboardingTour from "@/components/toolkit/ToolkitOnboardingTour";
import { ArrowRight, Sparkle } from "@phosphor-icons/react";

/**
 * /farmers/toolkit — Hub page that lists all 24 tools with a preview
 * screenshot each. Farmers can eyeball what every tool does before
 * they click in. Two categories (Farm Management · Marketing Suite)
 * plus a season toggle inside each.
 */
const FILTERS = [
  { key: "shipping", label: "Shipping today", match: (t) => t.status === "shipping" },
  { key: "soon",     label: "Coming soon",    match: (t) => t.status !== "shipping" },
  { key: "all",      label: "All tools",      match: () => true },
];

export default function FarmerToolkitHub() {
  const [category, setCategory] = useState("farm-management");
  const [filter, setFilter] = useState("all");
  const active = FILTERS.find((f) => f.key === filter) || FILTERS[2];
  const inCategory = TOOLKIT.filter((t) => t.category === category);
  const catMeta = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 px-5 md:px-10 bg-[#F5EBDC]" data-testid="farmer-toolkit-hub-page">
      <ToolkitOnboardingTour />
      <div className="max-w-6xl mx-auto"><FarmersNav /></div>

      <div className="max-w-6xl mx-auto mt-6">
        {/* Hero */}
        <header className="max-w-3xl">
          <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
            <Sparkle size={12} weight="fill" className="inline mr-1" /> Farmer Toolkit · 24 tools
          </div>
          <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[1.02] tracking-tight text-[#151312]">
            Grow it. Manage it. Sell it. Share it.
          </h1>
          <p className="mt-3 text-[#3A3830]">
            Twenty-four tools built for real farm days. Tap any tool to see a
            preview screen, how it works, and where it fits into your season.
          </p>

          {/* Category tabs */}
          <div className="mt-6 inline-flex items-center gap-1 rounded-full bg-[#151312] p-1" data-testid="toolkit-category">
            {CATEGORIES.map((c) => {
              const isActive = c.key === category;
              const count = TOOLKIT.filter((t) => t.category === c.key).length;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategory(c.key)}
                  data-testid={`toolkit-category-${c.key}`}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase transition ${
                    isActive
                      ? "bg-[color:var(--np-brand,#B85F2A)] text-white"
                      : "text-[#E7DDCA] hover:text-white"
                  }`}
                >
                  {c.label}
                  <span className={`rounded-full px-1.5 py-px text-[9px] ${isActive ? "bg-white/25 text-white" : "bg-white/10 text-[#E7DDCA]"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          <p className="mt-4 font-serif-display italic text-[#6C685E]">{catMeta.tagline}</p>

          {/* Season / status filter */}
          <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white p-1" data-testid="toolkit-filter">
            {FILTERS.map((f) => {
              const isActive = f.key === filter;
              const count = inCategory.filter(f.match).length;
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  data-testid={`toolkit-filter-${f.key}`}
                  className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.24em] uppercase transition ${
                    isActive
                      ? "bg-[#151312] text-[#F5EBDC]"
                      : "text-[#3A3830] hover:bg-black/5"
                  }`}
                >
                  {f.label}
                  <span className={`rounded-full px-1.5 py-px text-[9px] ${isActive ? "bg-[color:var(--np-brand,#B85F2A)] text-white" : "bg-black/5"}`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </header>

        {/* Stages (only meaningful for farm-management; marketing shows flat grid) */}
        {category === "farm-management" ? (
          STAGES.map((stage) => {
            const stageTools = inCategory.filter((t) => t.stage === stage.n).filter(active.match);
            if (stageTools.length === 0) return null;
            return (
              <section key={stage.n} className="mt-12" data-testid={`toolkit-stage-${stage.n}`}>
                <div className="flex items-baseline gap-3">
                  <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                    Stage {stage.n}
                  </span>
                  <h2 className="font-serif-display text-2xl md:text-3xl text-[#151312]">{stage.name}</h2>
                </div>
                <p className="mt-1 max-w-2xl text-[#3A3830]">{stage.description}</p>
                <ToolGrid tools={stageTools} />
              </section>
            );
          })
        ) : (
          <section className="mt-12" data-testid="toolkit-marketing-flat">
            <div className="flex items-baseline gap-3">
              <span className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                Marketing Suite
              </span>
              <h2 className="font-serif-display text-2xl md:text-3xl text-[#151312]">
                Your farm's AI-powered sales & marketing department
              </h2>
            </div>
            <p className="mt-1 max-w-3xl text-[#3A3830]">
              Find customers → promote harvests → sell more → build relationships → bring customers back.
            </p>
            <ToolGrid tools={inCategory.filter(active.match)} />
          </section>
        )}
      </div>
    </main>
  );
}

function ToolGrid({ tools }) {
  return (
    <div className="mt-6 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
      {tools.map((tool) => (
        <Link
          key={tool.slug}
          to={fpPath(`/toolkit/${tool.slug}`)}
          data-testid={`toolkit-card-${tool.slug}`}
          className="group rounded-3xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)] hover:shadow-sm transition"
        >
          <div className="flex items-start gap-3">
            <div className="shrink-0"><PhoneMock data={tool.mocks[0]} scale="sm" /></div>
            <div className="min-w-0 py-1">
              <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {TOOLKIT.indexOf(tool) + 1}
                <span className="ml-2 text-[#3A3830]/60">· {statusLabel(tool.status)}</span>
              </div>
              <div className="mt-1 font-serif-display text-xl text-[#151312] leading-tight">{tool.name}</div>
              <p className="mt-1 text-sm text-[#3A3830] leading-snug">{tool.tagline}</p>
              <div className="mt-3 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                See preview <ArrowRight size={10} weight="bold" />
              </div>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

function statusLabel(status) {
  if (status === "shipping") return "Shipping";
  if (status === "building") return "In build";
  return "Preview";
}
